import React, { useEffect, useState } from 'react';
import api from '../../api/apiClient';

type UnknownFeeRow = {
  _id: string;
  status?: string;
  paymentStatus?: string;
  dateTime?: string;
  service?: { name?: string };
  paymentDetails?: {
    settlementPaymentIntentId?: string;
    settlementAmount?: number;
    chargeReason?: string;
    stripeFeeUnknownAt?: string;
    platformShare?: number;
    stylistShare?: number;
  };
};

type TransferRow = {
  _id: string;
  userId?: string;
  reason?: string;
  service?: { name?: string };
  paymentDetails?: {
    chargeReason?: string;
    stylistTransferAmountCents?: number;
    stylistTransferError?: string;
    stylistTransferStatus?: string;
  };
};

const SystemHealthPage: React.FC = () => {
  const [apiOk, setApiOk] = useState<boolean | null>(null);
  const [unknownFeeCount, setUnknownFeeCount] = useState<number | null>(null);
  const [unknownFees, setUnknownFees] = useState<UnknownFeeRow[]>([]);
  const [reconciling, setReconciling] = useState(false);
  const [reconSummary, setReconSummary] = useState<string | null>(null);
  const [failedTransferCount, setFailedTransferCount] = useState<number | null>(null);
  const [heldTransferCount, setHeldTransferCount] = useState<number | null>(null);
  const [unsolicitedPayoutCount, setUnsolicitedPayoutCount] = useState<number | null>(null);
  const [failedTransfers, setFailedTransfers] = useState<TransferRow[]>([]);
  const [reconcilingTransfers, setReconcilingTransfers] = useState(false);
  const [transferReconSummary, setTransferReconSummary] = useState<string | null>(null);

  const loadUnknownFees = () =>
    api
      .get('/v1/admin/unknown-stripe-fees')
      .then((res) => {
        setUnknownFeeCount(res.data?.count ?? 0);
        setUnknownFees(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch(() => {
        setUnknownFeeCount(null);
        setUnknownFees([]);
      });

  const loadHeldTransfers = () =>
    api
      .get('/v1/admin/held-stylist-transfers')
      .then((res) => {
        setFailedTransferCount(res.data?.countFailed ?? 0);
        setHeldTransferCount(res.data?.countHeldUnonboarded ?? 0);
        setUnsolicitedPayoutCount(res.data?.countUnsolicitedPayouts7d ?? 0);
        setFailedTransfers(Array.isArray(res.data?.failed) ? res.data.failed : []);
      })
      .catch(() => {
        setFailedTransferCount(null);
        setHeldTransferCount(null);
        setUnsolicitedPayoutCount(null);
        setFailedTransfers([]);
      });

  useEffect(() => {
    api
      .get('/config/features')
      .then(() => setApiOk(true))
      .catch(() => setApiOk(false));
    loadUnknownFees();
    loadHeldTransfers();
  }, []);

  const runReconcile = async () => {
    setReconciling(true);
    setReconSummary(null);
    try {
      const res = await api.post('/v1/admin/unknown-stripe-fees/reconcile', { limit: 50 });
      const d = res.data?.data || {};
      setReconSummary(
        `Scanned ${d.scanned ?? 0}: ${d.settled ?? 0} settled, ${d.stillUnknown ?? 0} still unknown, ${d.failed ?? 0} failed.`
      );
      await loadUnknownFees();
    } catch (err: any) {
      setReconSummary(err?.response?.data?.message || err?.message || 'Reconcile failed');
    } finally {
      setReconciling(false);
    }
  };

  const env =
    process.env.NODE_ENV === 'production'
      ? 'production'
      : process.env.NODE_ENV === 'development'
        ? 'development'
        : process.env.NODE_ENV || 'unknown';

  return (
    <div style={{ padding: 24, maxWidth: 860 }}>
      <h1 style={{ marginBottom: 12 }}>System health</h1>
      <div
        style={{
          border: '1px solid #eee',
          borderRadius: 10,
          padding: 16,
          background: '#fff',
          lineHeight: 1.7,
        }}>
        <p>
          <strong>Environment:</strong> {env}
        </p>
        <p>
          <strong>Web app version:</strong>{' '}
          {process.env.REACT_APP_VERSION || process.env.npm_package_version || '0.1.0'}
        </p>
        <p>
          <strong>API reachability:</strong>{' '}
          {apiOk === null ? 'Checking…' : apiOk ? 'OK' : 'Failed'}
        </p>
      </div>

      <div
        style={{
          marginTop: 20,
          border: unknownFeeCount && unknownFeeCount > 0 ? '1px solid #F59E0B' : '1px solid #eee',
          borderRadius: 10,
          padding: 16,
          background: unknownFeeCount && unknownFeeCount > 0 ? '#FFFBEB' : '#fff',
        }}>
        <h2 style={{ marginTop: 0, marginBottom: 8, fontSize: 18 }}>
          Unsettled Stripe fees
        </h2>
        <p style={{ marginTop: 0 }}>
          Appointments charged at Stripe whose wallet split is blocked because the
          processing fee was not available at settle time (<code>stripeFeeUnknown</code>
          ). These do not look like a normal paid completion for payouts — no wallet
          credit has posted.
        </p>
        <p>
          <strong>Open count:</strong>{' '}
          {unknownFeeCount === null ? 'Unable to load' : unknownFeeCount}
        </p>
        <button
          type="button"
          onClick={runReconcile}
          disabled={reconciling}
          style={{
            padding: '8px 14px',
            borderRadius: 8,
            border: 'none',
            background: '#C9A84C',
            color: '#fff',
            fontWeight: 600,
            cursor: reconciling ? 'wait' : 'pointer',
          }}>
          {reconciling ? 'Reconciling…' : 'Retry fee lookup & split'}
        </button>
        {reconSummary ? <p style={{ marginTop: 10 }}>{reconSummary}</p> : null}
        {unknownFees.length > 0 ? (
          <ul style={{ marginTop: 16, paddingLeft: 18 }}>
            {unknownFees.map((row) => (
              <li key={row._id} style={{ marginBottom: 8 }}>
                <strong>{row._id}</strong>
                {row.service?.name ? ` · ${row.service.name}` : ''}
                {row.paymentDetails?.settlementAmount != null
                  ? ` · $${Number(row.paymentDetails.settlementAmount).toFixed(2)} charged`
                  : ''}
                {row.paymentDetails?.settlementPaymentIntentId
                  ? ` · ${row.paymentDetails.settlementPaymentIntentId}`
                  : ''}
                {row.paymentDetails?.chargeReason ? ` · ${row.paymentDetails.chargeReason}` : ''}
                {` · status ${row.status || '—'} / payment ${row.paymentStatus || '—'}`}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div
        style={{
          marginTop: 20,
          border: failedTransferCount && failedTransferCount > 0 ? '1px solid #F59E0B' : '1px solid #eee',
          borderRadius: 10,
          padding: 16,
          background: failedTransferCount && failedTransferCount > 0 ? '#FFFBEB' : '#fff',
        }}>
        <h2 style={{ marginTop: 0, marginBottom: 8, fontSize: 18 }}>
          Failed stylist Transfers
        </h2>
        <p style={{ marginTop: 0 }}>
          The client was charged and the wallet was credited, but the Stripe Transfer into
          the stylist’s Connect account has not succeeded yet. Retry does not re-charge
          the client.
        </p>
        <p>
          <strong>Failed count:</strong>{' '}
          {failedTransferCount === null ? 'Unable to load' : failedTransferCount}
          {' · '}
          <strong>Held (unonboarded):</strong>{' '}
          {heldTransferCount === null ? '—' : heldTransferCount}
          {' · '}
          <strong>Unsolicited payouts (7d):</strong>{' '}
          {unsolicitedPayoutCount === null ? '—' : unsolicitedPayoutCount}
        </p>
        <button
          type="button"
          onClick={async () => {
            setReconcilingTransfers(true);
            setTransferReconSummary(null);
            try {
              const res = await api.post('/v1/admin/held-stylist-transfers/reconcile', {
                limit: 50,
              });
              const d = res.data?.data || {};
              setTransferReconSummary(
                `Scanned ${d.scanned ?? 0}: ${d.transferred ?? 0} transferred, ${d.stillFailed ?? 0} still failed, ${d.stillHeld ?? 0} still held, ${d.failed ?? 0} errors.`
              );
              await loadHeldTransfers();
            } catch (err: any) {
              setTransferReconSummary(
                err?.response?.data?.message || err?.message || 'Reconcile failed'
              );
            } finally {
              setReconcilingTransfers(false);
            }
          }}
          disabled={reconcilingTransfers}
          style={{
            padding: '8px 14px',
            borderRadius: 8,
            border: 'none',
            background: '#C9A84C',
            color: '#fff',
            fontWeight: 600,
            cursor: reconcilingTransfers ? 'wait' : 'pointer',
          }}>
          {reconcilingTransfers ? 'Retrying…' : 'Retry failed Transfers'}
        </button>
        {transferReconSummary ? <p style={{ marginTop: 10 }}>{transferReconSummary}</p> : null}
        {failedTransfers.length > 0 ? (
          <ul style={{ marginTop: 16, paddingLeft: 18 }}>
            {failedTransfers.map((row) => (
              <li key={row._id} style={{ marginBottom: 8 }}>
                <strong>{row._id}</strong>
                {row.userId ? ` · user ${row.userId}` : ''}
                {row.service?.name ? ` · ${row.service.name}` : ''}
                {row.paymentDetails?.stylistTransferAmountCents != null
                  ? ` · $${(Number(row.paymentDetails.stylistTransferAmountCents) / 100).toFixed(2)}`
                  : ''}
                {row.paymentDetails?.chargeReason ? ` · ${row.paymentDetails.chargeReason}` : ''}
                {row.paymentDetails?.stylistTransferError
                  ? ` · ${row.paymentDetails.stylistTransferError}`
                  : ''}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

export default SystemHealthPage;
